import { Link } from "react-router-dom";
import { FaEye, FaStar, FaStarHalfAlt } from "react-icons/fa";
import Button from "../ui/Button";
import { useQuote } from "../../context/QuoteContext";
import { RichText, stripHtml } from "../../utils/richText";

export default function ProductCard({ product }) {
 const { addToQuote } = useQuote();
 const rating = Number(product.rating || 4.5);
 const displayPrice = Number(
  product.priceWithGst ?? product.finalPrice ?? product.price ?? 0,
 );
 const gstPercent = Number(product.gstPercent || 0);
 const availableStock = product.variants?.length
  ? product.variants.reduce(
     (total, variant) => total + Number(variant.stock || 0),
     0,
    )
  : Number(product.stock || 0);
 const isOutOfStock = availableStock <= 0;
 const displayUnit = product.variants?.[0]?.unit || product.unit || "";
 const isKgUnit = String(displayUnit).trim().toLowerCase() === "kg";

 return (
  <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
   <Link
    to={`/products/${product.id}`}
    className="relative aspect-[4/3] w-full overflow-hidden bg-white"
   >
    <img
     src={product.image}
     alt={stripHtml(product.name)}
     className="h-full w-full object-cover object-left transition duration-300 group-hover:scale-105"
    />
   </Link>
   <div className="flex flex-1 flex-col px-4 pb-4 pt-3">
    <h3 className="line-clamp-4 text-[18px] font-medium leading-6 text-[#0f5f99]">
     <Link
      to={`/products/${product.id}`}
      className="hover:text-secondary hover:underline"
     >
      <RichText value={product.name} />
     </Link>
    </h3>

    {displayPrice ? (
     <div className="mt-2 flex flex-wrap items-center gap-2">
      <span className="text-lg font-semibold text-slate-900">
       ₹{displayPrice.toLocaleString("en-IN")}{isKgUnit ? (
        <span className="ml-1 text-xs font-extrabold text-slate-500">/ kg</span>
       ) : null}
      </span>
      {product.hasDiscount ? (
       <>
        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500 line-through">
         ₹{Number(product.price || 0).toLocaleString("en-IN")}
        </span>
        <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-black text-success">
         {product.discountLabel}
        </span>
       </>
      ) : null}
      {gstPercent > 0 ? (
       <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-bold text-success">
        Incl. {gstPercent}% GST{isKgUnit ? " / kg" : ""}
       </span>
      ) : null}
     </div>
    ) : null}
    {false && product.price ? (
     <div className="mt-2 flex items-center gap-2">
      <span className="text-lg font-semibold text-slate-900">
       ₹{Number(product.price).toLocaleString("en-IN")}
      </span>

      <span className="text-sm text-slate-400 line-through">
       ₹
       {Number(product.oldPrice || product.price * 1.2).toLocaleString("en-IN")}
      </span>
     </div>
    ) : null}
    <div className="mt-1 flex items-center gap-1 text-sm">
     <span className="flex text-[#f97316]">
      <FaStar />
      <FaStar />
      <FaStar />
      <FaStar />
      <FaStarHalfAlt />
     </span>
     <span className="text-xs font-bold text-slate-500">
      {rating.toFixed(1)}
     </span>
    </div>
    <p className="mt-1 text-xs font-semibold text-slate-700">
     MOQ: {product.moq} - Stock: {availableStock}
    </p>
    {product.variants?.length ? (
     <p className="mt-1 line-clamp-1 text-xs font-bold text-primary">
      {product.optionGroupName || "Options"}:{" "}
      {product.variants
       .map((variant) => variant.label)
       .slice(0, 4)
       .join(", ")}
      {product.variants.length > 4 ? "..." : ""}
     </p>
    ) : null}
    {product.colorOptions?.length ? (
     <div className="mt-2 flex items-center gap-2">
      <span className="text-xs font-bold text-slate-500">
       {product.colorOptions.length} colors
      </span>
      <span className="flex gap-1.5">
       {product.colorOptions.map((color) => (
        <span
         key={color.name}
         className="h-4 w-4 rounded-full border border-slate-200 shadow-sm"
         style={{ backgroundColor: color.hex }}
         title={color.name}
        />
       ))}
      </span>
     </div>
    ) : null}
    <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-600">
     <RichText value={product.shortDescription} />
    </p>
    <div className="mt-auto grid grid-cols-2 gap-2 pt-3">
     <Button
      to={`/products/${product.id}`}
      variant="secondary"
      className="min-h-9 rounded-lg px-2 py-2 text-xs"
     >
      <FaEye /> Details
     </Button>
     <Button
      onClick={() => {
       if (!isOutOfStock) addToQuote(product);
      }}
      disabled={isOutOfStock}
      variant={isOutOfStock ? "secondary" : "primary"}
      className={`min-h-9 rounded-lg px-2 py-2 text-xs ${isOutOfStock ? "border-red-100 bg-red-50 text-danger hover:border-red-100 hover:text-danger" : ""}`}
     >
      {isOutOfStock ? "Out of stock" : "Quote"}
     </Button>
    </div>
   </div>
  </article>
 );
}



